import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { db, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { doc, setDoc, increment } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

export const useAnalytics = () => {
    const location = useLocation();
    const { user, loading } = useAuth();
    // Use sessionStorage to persist anonymous state across component remounts during login
    // Remove useRefs which might be lost during auth state changes if component remounts

    const trackedPath = useRef<string | null>(null);

    // Track if user was ever connected in this session (Logout Protection)
    const hasBeenConnected = useRef(false);

    useEffect(() => {
        // Ignore localhost/dev environments
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return;
        }

        // Wait for auth to be ready
        if (loading) return;

        const currentPath = location.pathname + location.search;
        const isConnected = !!user;
        const isAdmin = user?.role === 'admin';

        // --- LOGOUT PROTECTION ---
        if (isConnected) {
            hasBeenConnected.current = true;
        } else if (hasBeenConnected.current) {
            // User logged out: Do NOT track as a new anonymous visitor
            return;
        }

        // --- MIGRATION LOGIC (Anonymous -> Connected) ---
        const migrateStats = async () => {
            if (isConnected) {
                // 1. Migrate Flyer Scan if needed
                const pendingScan = sessionStorage.getItem('pending_anon_scan');
                if (pendingScan) {
                    try {
                        const { date } = JSON.parse(pendingScan);
                        const dailyRef = doc(db, 'analytics_daily', date);
                        const campaignRef = doc(db, 'analytics_campaigns', 'flyer');

                        // Decrement anonymous. 
                        // If Admin: DO NOT increment connected.
                        // If Not Admin: Increment connected.
                        const updates: any = {
                            flyer_scans_anonymous: increment(-1),
                        };
                        if (!isAdmin) {
                            updates.flyer_scans_logged_in = increment(1);
                        }

                        await setDoc(dailyRef, updates, { merge: true });

                        const campaignUpdates: any = {
                            total_scans_anonymous: increment(-1),
                        };
                        if (!isAdmin) {
                            campaignUpdates.total_scans_logged_in = increment(1);
                        }

                        await setDoc(campaignRef, campaignUpdates, { merge: true });

                        sessionStorage.removeItem('pending_anon_scan'); // Done
                    } catch (error) {
                        console.error("Error migrating flyer scan:", error);
                    }
                }

                // 2. Migrate Visit if needed
                const pendingVisit = sessionStorage.getItem('pending_anon_visit');
                if (pendingVisit) {
                    try {
                        const { date } = JSON.parse(pendingVisit);
                        const dailyRef = doc(db, 'analytics_daily', date);

                        // Decrement anonymous.
                        // If Admin: DO NOT increment connected.
                        // If Not Admin: Increment connected.
                        const updates: any = {
                            visits_anonymous: increment(-1),
                        };
                        if (!isAdmin) {
                            updates.visits_logged_in = increment(1);
                        }

                        await setDoc(dailyRef, updates, { merge: true });

                        sessionStorage.removeItem('pending_anon_visit'); // Done
                    } catch (error) {
                        console.error("Error migrating visit:", error);
                    }
                }

                // 3. Migrate Facebook Visit if needed
                const pendingFacebook = sessionStorage.getItem('pending_anon_facebook');
                if (pendingFacebook) {
                    try {
                        const { date } = JSON.parse(pendingFacebook);
                        const dailyRef = doc(db, 'analytics_daily', date);
                        const facebookRef = doc(db, 'analytics_campaigns', 'facebook');

                        // Decrement anonymous.
                        const updates: any = {
                            facebook_visits_anonymous: increment(-1),
                        };
                        const fbUpdates: any = {
                            total_visits_anonymous: increment(-1),
                        };

                        // If Not Admin: Increment connected.
                        // If Admin: Do nothing (just remove anonymous)
                        if (!isAdmin) {
                            updates.facebook_visits_logged_in = increment(1);
                            fbUpdates.total_visits_logged_in = increment(1);
                        }

                        await setDoc(dailyRef, updates, { merge: true });
                        await setDoc(facebookRef, fbUpdates, { merge: true });

                        sessionStorage.removeItem('pending_anon_facebook');
                    } catch (error) {
                        console.error("Error migrating facebook visit:", error);
                    }
                }
            }
        };

        migrateStats();

        // Avoid double tracking the same path + search combination
        if (trackedPath.current === currentPath) return;
        trackedPath.current = currentPath;

        // SKIP TRACKING FOR ADMINS (if already connected)
        if (isAdmin) return;

        // Log page view (Admins excluded above)
        logEvent(analytics, 'page_view', {
            page_path: currentPath,
            user_type: isConnected ? 'connected' : 'anonymous'
        });

        // Check for campaign parameters or special sources
        const searchParams = new URLSearchParams(location.search);
        const utmSource = searchParams.get('utm_source');
        const ref = searchParams.get('ref');
        const fbclid = searchParams.get('fbclid'); // Just in case it wasn't cleaned
        // Check if index.tsx detected a facebook source before cleaning
        const pendingSource = sessionStorage.getItem('analytics_pending_source');
        const isFacebookVisit = fbclid || pendingSource === 'facebook';

        const trackVisit = async () => {
            try {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const today = `${year}-${month}-${day}`; // YYYY-MM-DD (Local Time)
                const dailyRef = doc(db, 'analytics_daily', today);
                const campaignRef = doc(db, 'analytics_campaigns', 'flyer');
                const facebookRef = doc(db, 'analytics_campaigns', 'facebook');

                // Prepare updates for daily stats
                const dailyUpdates: any = {
                    date: today
                };

                // Track specific campaign sources
                if (utmSource === 'flyer' || ref === 'qr_code_flyer') {
                    // Update ref regarding anonymous scan
                    if (!isConnected) {
                        sessionStorage.setItem('pending_anon_scan', JSON.stringify({ date: today }));
                    }

                    logEvent(analytics, 'campaign_visit', {
                        source: 'flyer_qr_code',
                        medium: 'offline',
                        campaign: 'flyer_fevrier_2026',
                        user_type: isConnected ? 'connected' : 'anonymous'
                    });

                    // Increment daily flyer scans
                    dailyUpdates[isConnected ? 'flyer_scans_logged_in' : 'flyer_scans_anonymous'] = increment(1);

                    // Increment global flyer counter
                    const campaignUpdates: any = {
                        last_scan: new Date()
                    };
                    campaignUpdates[isConnected ? 'total_scans_logged_in' : 'total_scans_anonymous'] = increment(1);

                    await setDoc(campaignRef, campaignUpdates, { merge: true });

                    // Clean URL to prevent bookmarking with campaign params
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('utm_source');
                    newUrl.searchParams.delete('ref');
                    window.history.replaceState({ ...window.history.state }, '', newUrl.toString());

                } else if (isFacebookVisit) {
                    // FACEBOOK VISIT
                    logEvent(analytics, 'campaign_visit', {
                        source: 'facebook',
                        medium: 'social',
                        campaign: 'facebook_auto_tag',
                        user_type: isConnected ? 'connected' : 'anonymous'
                    });

                    // Increment daily facebook visits
                    dailyUpdates[isConnected ? 'facebook_visits_logged_in' : 'facebook_visits_anonymous'] = increment(1);

                    // Increment global facebook counter
                    const fbUpdates: any = {
                        last_visit: new Date()
                    };
                    fbUpdates[isConnected ? 'total_visits_logged_in' : 'total_visits_anonymous'] = increment(1);
                    await setDoc(facebookRef, fbUpdates, { merge: true });

                    // Clean pending source
                    sessionStorage.removeItem('analytics_pending_source');

                    // Update ref regarding anonymous facebook visit
                    if (!isConnected) {
                        sessionStorage.setItem('pending_anon_facebook', JSON.stringify({ date: today }));
                    }
                } else {
                    // Standard visit (NOT a flyer scan AND NOT Facebook)
                    dailyUpdates[isConnected ? 'visits_logged_in' : 'visits_anonymous'] = increment(1);

                    // Update ref regarding anonymous visit
                    if (!isConnected) {
                        sessionStorage.setItem('pending_anon_visit', JSON.stringify({ date: today, path: currentPath }));
                    }
                }

                // Apply daily updates
                await setDoc(dailyRef, dailyUpdates, { merge: true });

            } catch (error) {
                console.error("Error tracking visit:", error);
            }
        };

        trackVisit();
    }, [location, user, loading]);
};
